<script setup>
import axios from 'axios'
import { ref, computed, watch, nextTick } from 'vue'
import {
    PhX,
    PhArrowsOut,
    PhBank,
    PhBook,
    PhSpinner,
    PhWarning,
    PhCheckSquare,
    PhSquare,
    PhPrinter,
} from '@phosphor-icons/vue'

import CombinedTable from './CombinedTable.vue'
import { useResultStore } from '@/Stores/useResult'

const props = defineProps({
    open:      { type: Boolean,           default: false },
    classes:   { type: Array,             default: () => [] },
    terms:     { type: Array,             default: () => [] },
    sessions:  { type: Array,             default: () => [] },
    termId:    { type: [String, Number],  default: null },
    sessionId: { type: [String, Number],  default: null },
})

const emit = defineEmits(['close'])

const resultStore = useResultStore()

const COMBINED_KEY = 'Combined'

const selectedClassIds   = ref([])
const selectedSubjectIds = ref([])
const availableSubjects  = ref([])

const loadingSubjects = ref(false)
const loadingResults  = ref(false)

const subjectsError = ref(null)
const resultsError  = ref(null)

const results           = ref([])
const assessmentColumns = ref({})
const subjectList       = ref([])
const classList         = ref([])

const hasGenerated = ref(false)
const expanded     = ref(false)

const canFetchSubjects = computed(() =>
    selectedClassIds.value.length >= 1
)

const canGenerate = computed(() =>
    selectedClassIds.value.length >= 2 &&
    selectedSubjectIds.value.length >= 1
)

const allSubjectsSelected = computed(() =>
    availableSubjects.value.length > 0 &&
    availableSubjects.value.every(s => selectedSubjectIds.value.includes(s.id))
)

const selectedTerm = computed(() =>
    props.terms.find(t => String(t.id) === String(props.termId)) ?? null
)

const selectedSession = computed(() =>
    props.sessions.find(s => String(s.id) === String(props.sessionId)) ?? null
)

watch(() => props.open, (val) => {
    if (!val) resetState()
})

let subjectFetchTimer = null

watch(selectedClassIds, () => {
    selectedSubjectIds.value = []
    availableSubjects.value  = []
    results.value            = []
    hasGenerated.value       = false

    clearTimeout(subjectFetchTimer)

    if (canFetchSubjects.value) {
        subjectFetchTimer = setTimeout(fetchSubjects, 300)
    }
}, { deep: true })

function resetState() {
    selectedClassIds.value   = []
    selectedSubjectIds.value = []
    availableSubjects.value  = []
    results.value            = []
    assessmentColumns.value  = {}
    subjectList.value        = []
    classList.value          = []
    hasGenerated.value       = false
    expanded.value           = false
    loadingSubjects.value    = false
    loadingResults.value     = false
    subjectsError.value      = null
    resultsError.value       = null
}

function toggleClass(id) {
    const idx = selectedClassIds.value.indexOf(id)

    if (idx === -1) selectedClassIds.value.push(id)
    else selectedClassIds.value.splice(idx, 1)
}

function toggleSubject(id) {
    const idx = selectedSubjectIds.value.indexOf(id)

    if (idx === -1) selectedSubjectIds.value.push(id)
    else selectedSubjectIds.value.splice(idx, 1)
}

function toggleAllSubjects() {
    selectedSubjectIds.value = allSubjectsSelected.value
        ? []
        : availableSubjects.value.map(s => s.id)
}

async function fetchSubjects() {
    loadingSubjects.value = true
    subjectsError.value   = null

    try {
        const { data } = await axios.post(
            '/staff/manage-results/combined/subjects',
            {
                class_ids:  selectedClassIds.value,
                session_id: props.sessionId,
                term_id:    props.termId,
            }
        )

        availableSubjects.value = data.subjects ?? []

    } catch {
        subjectsError.value = 'Could not load subjects. Please try again.'
    } finally {
        loadingSubjects.value = false
    }
}

async function generate() {
    if (!canGenerate.value) return

    loadingResults.value = true
    resultsError.value   = null
    hasGenerated.value   = false

    try {
        const { data } = await axios.post(
            '/staff/manage-results/combined/generate',
            {
                class_ids:   selectedClassIds.value,
                subject_ids: selectedSubjectIds.value,
                session_id:  props.sessionId,
                term_id:     props.termId,
            }
        )

        results.value           = data.results           ?? []
        assessmentColumns.value = data.assessmentColumns ?? {}
        subjectList.value       = data.subjectList       ?? []
        classList.value         = data.classList         ?? []

        hasGenerated.value = true

        syncToStore(data)

        await nextTick()

        expanded.value = true

    } catch {
        resultsError.value = 'Failed to generate results. Please try again.'
    } finally {
        loadingResults.value = false
    }
}

function syncToStore(data) {
    const rawResults  = data.results ?? []
    const rawSubjects = data.subjectList ?? []

    const termName  = selectedTerm.value?.name    ?? ''
    const yearName  = selectedSession.value?.name ?? ''
    const termEndAt = selectedTerm.value?.ends_at ?? null

    const mapped = rawResults.map(r => ({
        student: {
            name: [
                r.student.first_name,
                r.student.other_name,
                r.student.last_name,
            ]
                .filter(Boolean)
                .join(' '),

            id: r.student.id,
        },

        form_master:  r.class_name ?? 'Combined',
        next_class:   null,
        section_name: '',

        subjects: rawSubjects.map(subject => {
            const assessments    = r.subject_scores[subject.id]?.assessments ?? {}
            const assessmentList = Object.values(assessments)

            const ca =
                assessmentList.find(a => a.name?.toLowerCase().includes('ca'))
                ?? assessmentList[0]
                ?? null

            const exam =
                assessmentList.find(a => a.name?.toLowerCase().includes('exam'))
                ?? assessmentList[1]
                ?? null

            return {
                subject:    subject.name,
                arabic:     subject.arabic ?? '',
                ca_score:   ca?.score   ?? null,
                exam_score: exam?.score ?? null,
            }
        }),

        total_score:   r.total_score,
        average_score: r.average,
        rank:          r.position_text,
    }))

    resultStore.setResults(
        { [COMBINED_KEY]: mapped },
        termName,
        yearName,
        termEndAt
    )
}

const handlePrintAll         = () => resultStore.generateResult(COMBINED_KEY)
const handlePrintClassReport = () => resultStore.generateResultSheet(COMBINED_KEY)
</script>

<template>
    <Transition name="backdrop">
        <div
            v-if="open"
            class="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-40"
            @click="emit('close')"
        />
    </Transition>

    <Transition name="drawer">
        <div
            v-if="open"
            :class="[
                'fixed top-0 right-0 h-full z-50 bg-white shadow-2xl flex flex-col',
                'transition-all duration-300 ease-out',
                expanded ? 'w-full' : 'w-full sm:w-[680px]',
            ]"
        >

            <!-- ── Header ── -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white shrink-0">
                <div class="flex items-center gap-3">
                    <div class="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <PhBank :size="18" class="text-primary" />
                    </div>

                    <div>
                        <h2 class="text-sm font-bold text-gray-900 tracking-tight">
                            Combined Classes Report
                        </h2>

                        <p class="text-xs text-gray-400 mt-0.5">
                            Merge &amp; rank students across classes
                        </p>
                    </div>
                </div>

                <div class="flex items-center gap-2">
                    <button
                        @click="expanded = !expanded"
                        class="p-2 rounded-lg text-gray-400 hover:text-primary hover:bg-gray-100 transition"
                        :title="expanded ? 'Compact view' : 'Expand full screen'"
                    >
                        <PhArrowsOut :size="16" />
                    </button>

                    <button
                        @click="emit('close')"
                        class="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
                    >
                        <PhX :size="16" />
                    </button>
                </div>
            </div>

            <!-- ── Body ── -->
            <div class="flex-1 overflow-y-auto">

                <!-- Config panel -->
                <div class="px-6 py-5 border-b border-gray-100 bg-gray-50/60 space-y-5">

                    <!-- Classes -->
                    <div>
                        <div class="flex items-center gap-2 mb-3">
                            <PhBank :size="14" class="text-primary" />

                            <span class="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Select Classes
                                <span class="ml-1 text-gray-400 font-normal normal-case">(minimum 2)</span>
                            </span>
                        </div>

                        <div class="flex flex-wrap gap-2">
                            <button
                                v-for="cls in classes"
                                :key="cls.id"
                                @click="toggleClass(cls.id)"
                                :class="[
                                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all',
                                    selectedClassIds.includes(cls.id)
                                        ? 'bg-primary text-white border-primary shadow-sm'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-primary hover:text-primary',
                                ]"
                            >
                                <PhCheckSquare v-if="selectedClassIds.includes(cls.id)" :size="12" />
                                <PhSquare v-else :size="12" />
                                {{ cls.name }}
                            </button>
                        </div>

                        <p v-if="selectedClassIds.length === 1" class="mt-2 text-xs text-amber-600">
                            Select at least one more class to combine.
                        </p>
                    </div>

                    <!-- Subjects -->
                    <Transition name="fade-slide">
                        <div v-if="canFetchSubjects">
                            <div class="flex items-center justify-between mb-3">
                                <div class="flex items-center gap-2">
                                    <PhBook :size="14" class="text-primary" />

                                    <span class="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Subjects to Include
                                    </span>
                                </div>

                                <button
                                    v-if="availableSubjects.length"
                                    @click="toggleAllSubjects"
                                    class="text-xs text-primary hover:underline font-medium"
                                >
                                    {{ allSubjectsSelected ? 'Deselect all' : 'Select all' }}
                                </button>
                            </div>

                            <div v-if="loadingSubjects" class="flex items-center gap-2 py-3 text-xs text-gray-400">
                                <PhSpinner :size="14" class="animate-spin" />
                                Loading subjects…
                            </div>

                            <div v-else-if="subjectsError" class="flex items-center gap-2 py-2 text-xs text-red-500">
                                <PhWarning :size="14" />
                                {{ subjectsError }}
                            </div>

                            <div v-else-if="!availableSubjects.length" class="text-xs text-gray-400 py-2">
                                No common subjects found for the selected classes &amp; term.
                            </div>

                            <div v-else class="flex flex-wrap gap-2">
                                <button
                                    v-for="sub in availableSubjects"
                                    :key="sub.id"
                                    @click="toggleSubject(sub.id)"
                                    :class="[
                                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all',
                                        selectedSubjectIds.includes(sub.id)
                                            ? 'bg-edit text-white border-edit shadow-sm'
                                            : 'bg-white text-gray-600 border-gray-200 hover:border-edit hover:text-edit',
                                    ]"
                                >
                                    <PhCheckSquare v-if="selectedSubjectIds.includes(sub.id)" :size="12" />
                                    <PhSquare v-else :size="12" />
                                    {{ sub.name }}
                                </button>
                            </div>
                        </div>
                    </Transition>

                    <!-- Actions row -->
                    <div class="flex flex-wrap items-center gap-3 pt-1">
                        <!-- Generate -->
                        <button
                            @click="generate"
                            :disabled="!canGenerate || loadingResults"
                            class="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold
                                   bg-primary text-white hover:bg-primary-light transition
                                   disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <PhSpinner v-if="loadingResults" :size="15" class="animate-spin" />
                            {{ loadingResults ? 'Generating…' : 'Generate Combined Report' }}
                        </button>

                        <!-- Print buttons — only visible after results are loaded -->
                        <template v-if="hasGenerated">
                            <button
                                @click="handlePrintAll"
                                :disabled="resultStore.isGenerating"
                                class="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold
                                       text-white bg-primary hover:bg-primary-light transition
                                       disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <PhPrinter :size="15" />
                                {{ resultStore.isGenerating ? 'Generating…' : 'Print All' }}
                            </button>

                            <button
                                @click="handlePrintClassReport"
                                :disabled="resultStore.isGenerating"
                                class="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold
                                       text-white bg-edit hover:bg-edit-light transition
                                       disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <PhPrinter :size="15" />
                                {{ resultStore.isGenerating ? 'Generating…' : 'Class Report' }}
                            </button>
                        </template>

                        <p v-if="!canGenerate && !loadingResults" class="text-xs text-gray-400">
                            <template v-if="selectedClassIds.length < 2">Pick ≥ 2 classes</template>
                            <template v-else-if="!selectedSubjectIds.length">Pick ≥ 1 subject</template>
                        </p>
                    </div>

                    <!-- Results error -->
                    <div v-if="resultsError" class="flex items-center gap-2 text-xs text-red-500">
                        <PhWarning :size="14" />
                        {{ resultsError }}
                    </div>
                </div>

                <!-- ── Results table ── -->
                <div v-if="hasGenerated" class="px-6 py-5">
                    <div class="flex items-center gap-2 mb-4 flex-wrap">
                        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-1">Classes:</span>
                        <span
                            v-for="cls in classList"
                            :key="cls.id"
                            class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20"
                        >
                            {{ cls.name }}
                        </span>
                        <span class="ml-auto text-xs text-gray-400">
                            {{ results.length }} student{{ results.length !== 1 ? 's' : '' }}
                        </span>
                    </div>

                    <CombinedTable
                        :results="results"
                        :assessment-columns="assessmentColumns"
                        :subject-list="subjectList"
                    />
                </div>

                <!-- Pre-generate empty state -->
                <div
                    v-else-if="!loadingResults && !hasGenerated && selectedClassIds.length >= 2"
                    class="flex flex-col items-center justify-center py-20 text-center px-6"
                >
                    <div class="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                        <PhBank :size="28" class="text-gray-300" />
                    </div>
                    <p class="text-sm text-gray-400">Select subjects and click Generate to see the combined report.</p>
                </div>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.backdrop-enter-active,
.backdrop-leave-active { transition: opacity 0.25s ease; }
.backdrop-enter-from,
.backdrop-leave-to     { opacity: 0; }

.drawer-enter-active,
.drawer-leave-active { transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.drawer-enter-from,
.drawer-leave-to     { transform: translateX(100%); }

.fade-slide-enter-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.fade-slide-enter-from   { opacity: 0; transform: translateY(-6px); }
.fade-slide-leave-active { transition: opacity 0.15s ease; }
.fade-slide-leave-to     { opacity: 0; }
</style>